import type { InboxPage, PendingInvitation } from "@repo/shared-types";

/**
 * The inbox: one per person, across every space they belong to.
 *
 * Opening it reads it — everything up to the newest line shown — so the
 * count in the header clears, while the lines that were new stay marked for
 * that visit. A notification written in between is newer than that line, and
 * stays unread.
 */

/**
 * What to mark read after showing a page: the newest line's moment, exactly
 * as the api gave it, or null when nothing on the page is new.
 */
export function readUpTo(page: InboxPage): string | null {
  if (!page.notifications.some((line) => !line.read)) return null;
  return page.notifications[0]?.createdAt ?? null;
}

/**
 * A page as it reads once marked read up to its newest line: every line on
 * it, since the page is newest first. What the cache holds after the inbox
 * has read it, so its copy is not drawn as new on the next visit.
 */
export function markedRead(page: InboxPage): InboxPage {
  return { ...page, notifications: page.notifications.map((line) => (line.read ? line : { ...line, read: true })) };
}

/**
 * The lines a page shows as new: those not read when they arrived. Kept for
 * the visit, so a line read by opening the inbox stays marked until it is
 * left.
 */
export function unreadOf(page: InboxPage): string[] {
  return page.notifications.filter((line) => !line.read).map((line) => line.id);
}

/**
 * What to mark seen after showing the invitations: the newest one's moment,
 * exactly as the api gave it (the list is newest first), or null when there
 * are none. An invitation is no notification — its invitee is not a member
 * yet — so the header counts it until the inbox has shown it.
 */
export function invitationsSeenUpTo(invitations: PendingInvitation[]): string | null {
  return invitations[0]?.createdAt ?? null;
}

/**
 * How long ago something happened, as an inbox says it: "just now", "5m",
 * "2h", "3d", "4w", "2y". Relative, so it reads the same in every time zone
 * and needs none.
 */
export function timeAgo(at: string, now: number): string {
  const seconds = Math.max(0, Math.floor((now - Date.parse(at)) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 365)}y`;
}
